import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/header.default.js';
import * as wireframeSkin from './skins/header.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

// Locked nav keys (must match the default skin's NAV_ITEMS).
export const HEADER_NAV_KEYS = Object.freeze([
  'home', 'docs', 'components', 'tokens',
  'guides', 'showcase', 'changelog', 'about',
]);
export const HEADER_THEMES = Object.freeze(['light', 'dark']);

class RunHeader extends RunElement {
  #currentSection = '';
  #theme = 'light';
  #rendering = false;
  #delegated = false;
  #slots = new Map();

  static get observedAttributes() {
    return ['current-section', 'theme'];
  }

  _onKeydownBound = (e) => this.#handleDocKeydown(e);
  _onShadowClickBound = (e) => this.#handleShadowClick(e);
  _onSlotChangeBound = (e) => this.#handleSlotChange(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'banner');
    // Cmd+K / Ctrl+K opens search globally (contract: document-level,
    // guarded against text-entry focus).
    document.addEventListener('keydown', this._onKeydownBound);
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeydownBound);
    this.#slots.forEach((slot) => {
      slot.removeEventListener('slotchange', this._onSlotChangeBound);
    });
    this.#slots.clear();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'current-section': this.currentSection = newValue ?? ''; break;
      case 'theme':           this.theme = newValue; break;
    }
  }

  get currentSection() { return this.#currentSection; }
  set currentSection(value) {
    const coerced = String(value ?? '').trim();
    if (this.getAttribute('current-section') !== coerced) {
      this.setAttribute('current-section', coerced);
    }
    if (this.#currentSection === coerced) return;
    this.#currentSection = coerced;
    this.requestUpdate();
  }

  get theme() { return this.#theme; }
  set theme(value) {
    const v = String(value ?? '').trim();
    const coerced = HEADER_THEMES.includes(v) ? v : 'light';
    if (this.#theme === coerced) return;
    this.#theme = coerced;
    if (this.getAttribute('theme') !== coerced) this.setAttribute('theme', coerced);
    this.requestUpdate();
  }

  // Read by the skin's render(): resolves the active nav key, or null when
  // currentSection is empty / not one of the locked keys.
  get _activeKey() {
    return HEADER_NAV_KEYS.includes(this.#currentSection)
      ? this.#currentSection
      : null;
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    // Delegated listener lives on the shadow root, which survives the
    // innerHTML swaps the skin performs on each render.
    if (!this.#delegated) {
      this.shadowRoot.addEventListener('click', this._onShadowClickBound);
      this.#delegated = true;
    }
  }

  firstUpdated() {
    this.shadowRoot.querySelectorAll('slot[name]').forEach((slot) => {
      this.#slots.set(slot.name, slot);
      slot.addEventListener('slotchange', this._onSlotChangeBound);
      const assigned = slot.assignedElements({ flatten: true });
      this.toggleAttribute(`data-has-${slot.name}`, assigned.length > 0);
    });
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #isTextEntryFocused() {
    const a = document.activeElement;
    if (!a) return false;
    const tag = a.tagName;
    // Check isContentEditable AND the raw attribute because jsdom may not set
    // isContentEditable=true for contenteditable="" (empty-string form).
    const ce = typeof a.getAttribute === 'function' ? a.getAttribute('contenteditable') : null;
    return (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      a.isContentEditable === true ||
      (ce !== null && ce !== 'false')
    );
  }

  #handleDocKeydown(event) {
    const isK = event.key === 'k' || event.key === 'K';
    if (!isK || !(event.metaKey || event.ctrlKey)) return;
    if (this.#isTextEntryFocused()) return;
    event.preventDefault();
    this.#emitSearchOpen();
  }

  #handleShadowClick(event) {
    const path = event.composedPath();
    const search = path.find(
      (n) => n.nodeType === 1 && n.classList?.contains('search-trigger'),
    );
    if (search) { this.#emitSearchOpen(); return; }

    const toggle = path.find(
      (n) => n.nodeType === 1 && n.classList?.contains('theme-toggle'),
    );
    if (toggle) { this.#cycleTheme(); return; }

    const navLink = path.find(
      (n) => n.nodeType === 1 && n.classList?.contains('nav-link') && n.dataset?.key,
    );
    if (navLink) {
      // Default navigation is allowed to proceed (contract): no preventDefault.
      this.dispatchEvent(new CustomEvent('run-header-nav-select', {
        bubbles: true,
        composed: true,
        detail: { key: navLink.dataset.key },
      }));
    }
  }

  #cycleTheme() {
    const next = this.#theme === 'light' ? 'dark' : 'light';
    this.theme = next; // updates property + re-renders before dispatch
    this.dispatchEvent(new CustomEvent('run-header-theme-change', {
      bubbles: true,
      composed: true,
      detail: { theme: next },
    }));
  }

  #emitSearchOpen() {
    this.dispatchEvent(new CustomEvent('run-header-search-open', {
      bubbles: true,
      composed: true,
      detail: {},
    }));
  }

  #handleSlotChange(event) {
    const slot = /** @type {HTMLSlotElement} */ (event.target);
    const assigned = slot.assignedElements({ flatten: true });
    this.toggleAttribute(`data-has-${slot.name}`, assigned.length > 0);
  }
}

if (!customElements.get('run-header')) {
  customElements.define('run-header', RunHeader);
}

export { RunHeader };
export default RunHeader;
